import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../../lib/auth';
import { ParentShell } from '../components/ParentShell';
import { learnerNavFromSummary } from '../lib/parent-portal-api';
import { fetchMyLearners } from '../lib/registration-api';

const SUPPORT = {
  email: 'info@21kschool.com',
  phone: '+91 96632 13636',
  whatsapp: '+91 96632 13636',
  hours: [
    'Monday – Friday, 9:00 AM – 7:00 PM IST',
    'Saturday, 10:00 AM – 4:00 PM IST',
  ],
};

/** wa.me takes digits only — no plus, no spaces. */
function waLink(number: string): string {
  return `https://wa.me/${number.replace(/\D/g, '')}`;
}

const FAQS: { question: string; answer: string }[] = [
  {
    question: 'How do I change my email or phone number?',
    answer:
      'Your mobile and WhatsApp numbers are yours to change: open My profile, edit them and save. Your email address is a different matter — it is what you sign in with, so it cannot be changed from that page. Contact us on any of the channels above and we will update it for you.',
  },
  {
    question: 'How do I add another child to my account?',
    answer:
      'Use "Add a child" at the bottom of the list in the sidebar. That starts a fresh registration, and once it is submitted the new child appears alongside your others with their own status.',
  },
  {
    question: 'What do the labels beside my children in the sidebar mean?',
    answer:
      'They are the short version of where each child has got to. "Choose subjects" and "Needs you" mean something is waiting on you. "Under review" means it is with the school. "Fee due" means a payment is outstanding. "Fee paid" means the admission fee is settled, and "Confirmed" means admission is fully confirmed. "Incomplete" means the registration was started but never submitted.',
  },
  {
    question: 'Can I change my child\u2019s subject choices after submitting them?',
    answer:
      'Not from the portal. Once you submit, the selection goes to the school for review and is locked so that it cannot change underneath the people assessing it. If something needs to change, contact us as soon as you can and tell us your child\u2019s enrolment number.',
  },
  {
    question: 'Where do I find a receipt for a payment I have made?',
    answer:
      'Open Payments. Every transaction is listed there with the amount and what it was for, and each completed payment has its own receipt you can open and print.',
  },
  {
    question: 'Are Cambridge examination fees included in what I pay here?',
    answer:
      'No. Cambridge assessment fees are set and collected by Cambridge International directly, and are not part of the Learn Plus fee shown in your quotation or on your receipt. You will see this stated wherever fees appear.',
  },
];

function MailIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function ChatIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.4 8.4 0 0 1 12.5 3 8.4 8.4 0 0 1 21 11.5z" />
    </svg>
  );
}

function PhoneIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

function Chevron(): ReactElement {
  return (
    <svg className="faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function HelpPage(): ReactElement {
  const { token, loading } = useAuth();

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: Boolean(token),
  });

  const learners = learnerNavFromSummary(learnersQuery.data ?? []);

  if (!loading && !token) return <Navigate to="/login" replace />;

  return (
    <ParentShell learners={learners} title="Help & support">
      <div className="page-header">
        <p className="lede">
          Talk to someone about your child’s registration, or find the answer to
          something the portal does not explain on its own.
        </p>
        <ul className="support-hours">
          {SUPPORT.hours.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <div className="stack-lg">
        <section className="stack">
          <h2 className="h3">Contact us</h2>
          <div className="contact-grid">
            <a className="contact-card" href={`mailto:${SUPPORT.email}`}>
              <span className="contact-icon" aria-hidden="true">
                <MailIcon />
              </span>
              <span className="contact-card-text">
                <span className="contact-card-label">Email us</span>
                <span className="contact-card-value">Email support</span>
              </span>
            </a>

            <a
              className="contact-card"
              href={waLink(SUPPORT.whatsapp)}
              target="_blank"
              rel="noreferrer"
            >
              <span className="contact-icon contact-icon-whatsapp" aria-hidden="true">
                <ChatIcon />
              </span>
              <span className="contact-card-text">
                <span className="contact-card-label">WhatsApp us</span>
                <span className="contact-card-value">{SUPPORT.whatsapp}</span>
              </span>
            </a>

            <a className="contact-card" href={`tel:${SUPPORT.phone.replace(/\s/g, '')}`}>
              <span className="contact-icon contact-icon-phone" aria-hidden="true">
                <PhoneIcon />
              </span>
              <span className="contact-card-text">
                <span className="contact-card-label">Call us</span>
                <span className="contact-card-value">{SUPPORT.phone}</span>
              </span>
            </a>
          </div>
        </section>

        <section className="stack">
          <h2 className="h3">Frequently asked questions</h2>
          <div className="faq-list">
            {FAQS.map((faq, index) => (
              <details className="faq-item" key={faq.question} open={index === 0}>
                <summary className="faq-question">
                  <span>{faq.question}</span>
                  <Chevron />
                </summary>
                <p className="faq-answer">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </ParentShell>
  );
}
